import { wrapError } from '@edu-frontend/modules/src/shared/lib/error';
import CyrillicToTranslit from 'cyrillic-to-translit-js';
import { showNetworkError } from 'platform-components';
import { commonDownloadFile, getStudentDisplayName } from 'platform-components/utils';
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { Student } from 'common.components/typings';
import { IntlShape } from 'react-intl';

export const downloadFile = async (filePath: string, fileName: string): Promise<void> => {
    const cyrillicToTranslit = CyrillicToTranslit();
    const translitName = cyrillicToTranslit.transform(fileName).replace(/\s+/g, '_');

    return commonDownloadFile(filePath, translitName);
};

export const downloadArchive = async (files: string[], fileName: string): Promise<void> => {
    if (isSSR) {
        return;
    }

    const response = await fetch('/compress-files/', {
        method: 'post',
        body: JSON.stringify(files),
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const cyrillicToTranslit = CyrillicToTranslit();
    a.href = url;
    a.download = cyrillicToTranslit.transform(fileName).replace(/\s+/g, '_');
    document.body.appendChild(a);
    a.click();
    a.remove();
};

export function buildCertFilename(intl: IntlShape, student: Student, ext?: `.${string}`) {
    const res: string = intl.formatMessage(
        { id: 'studentCertificate.fileName' },
        {
            student: getStudentDisplayName(student),
        }
    );

    return ext && !res.endsWith(ext) ? res + ext : res;
}

export function buildFallbalckCertFilename(
    intl: IntlShape,
    studentId: number | string,
    args: unknown,
    ext?: `.${string}`
) {
    try {
        return buildCertFilename(intl, args as Student, ext);
    } catch (err) {
        setTimeout(() => {
            throw err;
        });

        return studentId + (ext || '.pdf');
    }
}

function errorInfo(err: unknown) {
    let json: string | null = null;

    try {
        json = JSON.stringify(err);
    } catch {
        // ignore
    }

    let keys: string[][] | null = null;

    if (err && typeof err === 'object') {
        keys = [];

        for (let x = err; x; x = Object.getPrototypeOf(x)) {
            keys.push(Object.getOwnPropertyNames(x));
            keys.push(Object.getOwnPropertySymbols(x).map(String));
        }
    }

    if (err instanceof Error) {
        return {
            name: err.name,
            message: err.message,
            cause: err.cause,
            stack: err.stack,
            keys,
            json,
        };
    }

    return {
        typeof: typeof err,
        keys,
        json,
    };
}

export function useOnPdfGenerationError() {
    const dispatch = useDispatch();

    return useCallback(
        (messageId: string, error: unknown, additional: Record<string, unknown>) => {
            window.Ya.Rum.logError(
                {
                    message: messageId,
                    additional: {
                        _error: errorInfo(error),
                        ...additional,
                    },
                },
                wrapError(error)
            );

            dispatch(showNetworkError(error));
        },
        [dispatch]
    );
}
